import { FC, useCallback, useMemo, type CSSProperties } from 'react'
import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import cn from 'classnames'
import type { Field } from '@/api/types/crm/crm.types'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import { useAddAlert } from '@/hooks/useAddAlert'
import {
  composeCrmLeadIntegrationLinkFromInput,
  findLeadChatForIntegrationField,
  getCrmLeadFieldIntegrationBindings,
  getCrmLeadIntegrationFieldHint,
  getCrmLeadIntegrationFieldLabel,
  getCrmLeadIntegrationFieldMultiBindingWarning,
  getCrmLeadIntegrationFieldPlaceholder,
  getCrmLeadIntegrationFieldPrefixTailPlaceholder,
  getCrmLeadIntegrationFieldProviderKey,
  getCrmLeadIntegrationFieldUrlPrefix,
  isCrmLeadIntegrationTelegramChatField,
  resolveCrmLeadIntegrationLinkForCopy,
  splitCrmLeadIntegrationLinkForInput,
  TELEGRAM_CHAT_REQUIRES_INCOMING_HINT,
} from '@pages/Admin/CRM/utils/crmLeadIntegrationFields'
import { CRM_INTEGRATION_CHAT_PROVIDERS } from '@pages/Admin/CRM/components/PhoneIntegrationButtons/integrations'
import { formatCrmChatUnreadBadge } from '@pages/Admin/CRM/utils/leadFields'
import { isIntegrationCrmAccessible } from '@pages/Admin/CRM/utils/crmIntegrationAccess'
import { useCrmIntegrationChatAvailability } from '@/hooks/useCrmIntegrationChatAvailability'
import Input from '@pages/Admin/CRM/components/base/Input/Input'
import compactFieldClasses from '@pages/Admin/CRM/styles/compactLeadFields.module.css'
import chatBtnClasses from '../../../../CrmProviderChatButton/index.module.css'
import LeadField from '../../../components/LeadField/LeadField'
import classes from './index.module.css'

type OpenChatHandler = (
  leadId: number,
  options?: { conversationId?: number },
) => void

type LeadIntegrationFieldRowProps = {
  field: Field
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  lead: unknown
  leadId: number
  onOpenVkChatWithLead?: OpenChatHandler
  onOpenMaxChatWithLead?: OpenChatHandler
  onOpenTelegramChatWithLead?: OpenChatHandler
  onOpenWazzupChatWithLead?: OpenChatHandler
}

const openChatByProvider = (
  provider: CrmChatProviderKey,
  leadId: number,
  conversationId: number,
  handlers: {
    onOpenVkChatWithLead?: OpenChatHandler
    onOpenMaxChatWithLead?: OpenChatHandler
    onOpenTelegramChatWithLead?: OpenChatHandler
    onOpenWazzupChatWithLead?: OpenChatHandler
  },
) => {
  const options = { conversationId }
  if (provider === 'vkontakte') {
    handlers.onOpenVkChatWithLead?.(leadId, options)
    return
  }
  if (provider === 'max') {
    handlers.onOpenMaxChatWithLead?.(leadId, options)
    return
  }
  if (provider === 'telegram') {
    handlers.onOpenTelegramChatWithLead?.(leadId, options)
    return
  }
  if (provider === 'wazzup') {
    handlers.onOpenWazzupChatWithLead?.(leadId, options)
  }
}

const LeadIntegrationFieldRow: FC<LeadIntegrationFieldRowProps> = ({
  field,
  value,
  onChange,
  disabled = false,
  lead,
  leadId,
  onOpenVkChatWithLead,
  onOpenMaxChatWithLead,
  onOpenTelegramChatWithLead,
  onOpenWazzupChatWithLead,
}) => {
  const addAlert = useAddAlert()
  const { connectedIntegrations } = useCrmIntegrationChatAvailability()
  const label = getCrmLeadIntegrationFieldLabel(field)
  const hint = getCrmLeadIntegrationFieldHint(field)
  const placeholder = getCrmLeadIntegrationFieldPlaceholder(field)
  const urlPrefix = getCrmLeadIntegrationFieldUrlPrefix(field)
  const multiBindingWarning =
    getCrmLeadIntegrationFieldMultiBindingWarning(field)
  const isReadOnly = Boolean(disabled || multiBindingWarning)

  const linkParts = useMemo(
    () => splitCrmLeadIntegrationLinkForInput(value, urlPrefix),
    [urlPrefix, value],
  )
  const tailPlaceholder = getCrmLeadIntegrationFieldPrefixTailPlaceholder(
    urlPrefix,
  )
  const copyValue = useMemo(
    () => resolveCrmLeadIntegrationLinkForCopy(value, urlPrefix),
    [urlPrefix, value],
  )

  const providerMeta = useMemo(() => {
    const providerKey = getCrmLeadIntegrationFieldProviderKey(field)
    if (!providerKey) return null
    return (
      CRM_INTEGRATION_CHAT_PROVIDERS.find(
        (item) => item.provider === providerKey,
      ) ?? null
    )
  }, [field])

  const ProviderIcon = providerMeta?.icon ?? null
  const chatTarget = useMemo(
    () => findLeadChatForIntegrationField(lead, field),
    [field, lead],
  )

  const binding = getCrmLeadFieldIntegrationBindings(field)[0]
  const integrationItem =
    binding != null
      ? connectedIntegrations.find(
          (item) => Number(item.id) === Number(binding.integration_id),
        )
      : undefined
  const isAccessible =
    integrationItem == null || isIntegrationCrmAccessible(integrationItem)

  const canOpenChat = Boolean(chatTarget && isAccessible)
  const unreadLabel = canOpenChat
    ? formatCrmChatUnreadBadge(chatTarget?.unreadCount ?? 0)
    : null

  const isTelegramChatField = isCrmLeadIntegrationTelegramChatField(field)

  const disabledTooltip = !chatTarget
    ? isTelegramChatField
      ? `Нет чата по этому полю. ${TELEGRAM_CHAT_REQUIRES_INCOMING_HINT}`
      : 'Нет чата по этому полю'
    : !isAccessible
      ? 'Нет доступа к чатам этой интеграции'
      : null

  const iconStyle: CSSProperties = {
    fill: canOpenChat
      ? providerMeta?.color ?? 'var(--color-accent-vivid)'
      : 'var(--color-button-icon-disabled-soft)',
  }

  const handleCopy = useCallback(async () => {
    if (!copyValue) return

    try {
      await navigator.clipboard.writeText(copyValue)
      addAlert({
        title: 'Успешно',
        text: 'Ссылка скопирована в буфер обмена',
        type: 'success',
      })
    } catch {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось скопировать ссылку',
        type: 'error',
      })
    }
  }, [addAlert, copyValue])

  const copyButton = copyValue ? (
    <Tooltip title="Скопировать ссылку" placement="top">
      <button
        type="button"
        className={classes.copyButton}
        aria-label={`Скопировать ссылку: ${label}`}
        onClick={() => void handleCopy()}
      >
        <CopyOutlined
          className={classes.copyIcon}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      </button>
    </Tooltip>
  ) : null

  const chatButton = ProviderIcon ? (
    <div
      className={cn(
        chatBtnClasses.linkWrapper,
        chatBtnClasses.chatLinkWrapper,
        chatBtnClasses.linkWrapper_card,
        classes.chatButton,
      )}
      style={{ cursor: canOpenChat ? 'pointer' : 'default' }}
      role="button"
      tabIndex={canOpenChat ? 0 : -1}
      aria-label={canOpenChat ? `Открыть чат: ${label}` : label}
      aria-disabled={!canOpenChat}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        if (!chatTarget || !canOpenChat) return
        openChatByProvider(chatTarget.provider, leadId, chatTarget.conversationId, {
          onOpenVkChatWithLead,
          onOpenMaxChatWithLead,
          onOpenTelegramChatWithLead,
          onOpenWazzupChatWithLead,
        })
      }}
      onKeyDown={(event) => {
        if (!canOpenChat || !chatTarget) return
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        event.stopPropagation()
        openChatByProvider(chatTarget.provider, leadId, chatTarget.conversationId, {
          onOpenVkChatWithLead,
          onOpenMaxChatWithLead,
          onOpenTelegramChatWithLead,
          onOpenWazzupChatWithLead,
        })
      }}
    >
      <ProviderIcon style={iconStyle} aria-hidden />
      {unreadLabel ? (
        <span
          className={cn(
            chatBtnClasses.unreadBadge,
            chatBtnClasses.unreadBadge_card,
          )}
          aria-hidden
        >
          {unreadLabel}
        </span>
      ) : null}
    </div>
  ) : null

  const handlePrefixedInputChange = (nextInputValue: string) => {
    if (isReadOnly) return
    onChange(composeCrmLeadIntegrationLinkFromInput(nextInputValue, urlPrefix))
  }

  const fieldControl =
    urlPrefix && linkParts.showPrefix ? (
      <div
        className={cn(classes.prefixedControl, {
          [classes.prefixedControlDisabled]: isReadOnly,
        })}
      >
        <span className={classes.urlPrefix} title={urlPrefix}>
          {urlPrefix}
        </span>
        <Input
          size="small"
          className={classes.prefixedInput}
          value={linkParts.inputValue}
          disabled={isReadOnly}
          placeholder={tailPlaceholder || placeholder}
          aria-label={label}
          title={hint || label}
          onChange={(event) => handlePrefixedInputChange(event.target.value)}
        />
        {copyButton}
      </div>
    ) : (
      <div className={classes.plainControl}>
        <div className={classes.plainControlInput}>
          <LeadField
            field={field}
            size="small"
            value={value}
            onChange={onChange}
            disabled={isReadOnly}
            placeholder={placeholder}
            tooltipTitle={hint || label}
          />
        </div>
        {copyButton}
      </div>
    )

  return (
    <div className={classes.fieldItem}>
      <div className={classes.header}>
        {ProviderIcon ? (
          <ProviderIcon className={classes.providerIcon} aria-hidden />
        ) : null}
        <span className={classes.label} title={label}>
          {label}
        </span>
        {hint ? (
          <Tooltip placement="top" title={hint}>
            <InfoCircleOutlined
              className={classes.infoIcon}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </Tooltip>
        ) : null}
      </div>
      <div className={classes.inputRow}>
        <div className={classes.inputGrow}>{fieldControl}</div>
        {chatButton ? (
          <Tooltip
            placement="top"
            title={canOpenChat ? `Открыть чат: ${label}` : disabledTooltip}
          >
            {chatButton}
          </Tooltip>
        ) : null}
      </div>
      {multiBindingWarning ? (
        <p className={classes.warning}>{multiBindingWarning}</p>
      ) : null}
    </div>
  )
}

type LeadIntegrationFieldsBlockProps = {
  fields: Field[]
  fieldsData: Record<string, string>
  updateFieldData: (uniqueNameField: string, value: string) => void
  disabled?: boolean
  className?: string
  /** Правая колонка карточки лида — всегда один столбец. */
  layout?: 'default' | 'sidebar'
  lead: unknown
  leadId: number
  onOpenVkChatWithLead?: OpenChatHandler
  onOpenMaxChatWithLead?: OpenChatHandler
  onOpenTelegramChatWithLead?: OpenChatHandler
  onOpenWazzupChatWithLead?: OpenChatHandler
}

const LeadIntegrationFieldsBlock: FC<LeadIntegrationFieldsBlockProps> = ({
  fields,
  fieldsData,
  updateFieldData,
  disabled = false,
  className,
  layout = 'default',
  lead,
  leadId,
  onOpenVkChatWithLead,
  onOpenMaxChatWithLead,
  onOpenTelegramChatWithLead,
  onOpenWazzupChatWithLead,
}) => {
  if (fields.length === 0) return null

  return (
    <div
      className={cn(
        classes.grid,
        layout === 'sidebar' && classes.gridSidebar,
        compactFieldClasses.root,
        className,
      )}
    >
      {fields.map((field) => (
        <LeadIntegrationFieldRow
          key={field.id}
          field={field}
          value={fieldsData[field.unique_name] ?? ''}
          onChange={(nextValue) =>
            updateFieldData(field.unique_name, nextValue)
          }
          disabled={disabled}
          lead={lead}
          leadId={leadId}
          onOpenVkChatWithLead={onOpenVkChatWithLead}
          onOpenMaxChatWithLead={onOpenMaxChatWithLead}
          onOpenTelegramChatWithLead={onOpenTelegramChatWithLead}
          onOpenWazzupChatWithLead={onOpenWazzupChatWithLead}
        />
      ))}
    </div>
  )
}

export default LeadIntegrationFieldsBlock
